import { type ReactNode, useEffect } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Globe } from 'lucide-react'
import type { EventTheme } from '@/lib/registrationFormShared'

const LOCALE_LABELS: Record<string, string> = { en: 'English', ar: 'العربية', fr: 'Français', es: 'Español' }

function registrationLinkClass(slug?: string | null) {
  const normalized = (slug || '').trim().toLowerCase()
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) return ''
  return `ev-reg-link-${normalized}`
}

export type RegistrationPageVariant = 'landing' | 'form' | 'agenda' | 'confirmation'

type Placement = 'inside' | 'outside'

interface Props extends Omit<AppShellProps, 'children'> {
  event: EventTheme
  currentLocale: string
  locales: string[]
  urls: AppShellProps['urls']
  translations: AppShellProps['translations'] & { register: string }
  pageTitle?: string
  pageVariant?: RegistrationPageVariant
  registrationLinkSlug?: string | null
  children: ReactNode
}

const headerClassName =
  'ev-reg-header mb-6 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_p]:leading-relaxed [&_p]:text-sm'

const footerClassName =
  'ev-reg-footer mt-6 [&_a]:text-[var(--ev-primary)] [&_a]:underline [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5'

function RegistrationHeader({ event, placement }: { event: EventTheme; placement: Placement }) {
  const placementClass = placement === 'outside' ? 'ev-reg-header-outside' : 'ev-reg-header-inside'

  if (event.regHeaderHtml) {
    return (
      <div
        className={`${headerClassName} ${placementClass}`.trim()}
        dangerouslySetInnerHTML={{ __html: event.regHeaderHtml }}
      />
    )
  }

  return (
    <div className={`ev-reg-default-header mb-6 text-center ${placementClass}`.trim()}>
      <h1 className="ev-reg-event-title mb-2 text-2xl font-bold" style={{ color: 'var(--ev-primary)' }}>
        {event.name}
      </h1>
      {event.about && (
        <div
          className="ev-reg-event-about text-sm leading-relaxed opacity-70"
          dangerouslySetInnerHTML={{ __html: event.about }}
        />
      )}
    </div>
  )
}

function RegistrationFooter({ event, placement }: { event: EventTheme; placement: Placement }) {
  if (!event.regFooterHtml) return null

  const placementClass = placement === 'outside' ? 'ev-reg-footer-outside' : 'ev-reg-footer-inside'

  return (
    <div
      className={`${footerClassName} ${placementClass}`.trim()}
      dangerouslySetInnerHTML={{ __html: event.regFooterHtml }}
    />
  )
}

function LangSwitcher({
  locales,
  currentLocale,
  switchLocale,
}: {
  locales: string[]
  currentLocale: string
  switchLocale: (locale: string) => void
}) {
  return (
    <div className="ev-reg-lang-switcher-wrap px-4 pt-3">
      <div className="ev-reg-lang-switcher mx-auto flex max-w-2xl justify-end">
        <div
          className="ev-reg-lang-switcher-inner inline-flex items-center gap-1.5 rounded-md border px-2 py-1"
          style={{ borderColor: 'var(--ev-input-border)', backgroundColor: 'var(--ev-card-bg)' }}
        >
          <Globe className="ev-reg-lang-switcher-icon h-3.5 w-3.5 opacity-50" />
          {locales.map((loc) => (
            <button
              key={loc}
              type="button"
              onClick={() => switchLocale(loc)}
              className={`ev-reg-lang-option ev-reg-lang-${loc} rounded px-2 py-0.5 text-xs font-medium transition-colors ${loc === currentLocale ? 'ev-reg-lang-active opacity-100' : 'opacity-50 hover:opacity-80'}`}
              style={{
                color: loc === currentLocale ? 'var(--ev-btn-text)' : 'var(--ev-text)',
                backgroundColor: loc === currentLocale ? 'var(--ev-btn)' : 'transparent',
              }}
            >
              {LOCALE_LABELS[loc] || loc.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function RegistrationPageLayout({
  event,
  currentLocale,
  locales,
  urls,
  translations: t,
  pageTitle,
  pageVariant,
  registrationLinkSlug,
  children,
  ...shell
}: Props) {
  useEffect(() => {
    const dir = currentLocale === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.dir = dir
    document.documentElement.lang = currentLocale
    document.body.dir = dir
  }, [currentLocale])

  const resolvedFontFamily = event.customFontUrl
    ? (event.customFontName || 'EventCustomFont')
    : event.font || undefined
  const mobileBackgroundUrl = event.mobileDesignUrl || event.desktopDesignUrl
  const desktopBackgroundUrl = event.desktopDesignUrl || event.mobileDesignUrl
  const mobileBannerUrl = event.mobileBannerUrl || event.bannerUrl
  const desktopBannerUrl = event.bannerUrl || event.mobileBannerUrl
  const hasBackground = Boolean(desktopBackgroundUrl || mobileBackgroundUrl)
  const hasBanner = Boolean(desktopBannerUrl || mobileBannerUrl)
  const useWideShell = hasBackground || hasBanner
  const headerPlacement: Placement = event.regHeaderPlacement === 'outside' ? 'outside' : 'inside'
  const footerPlacement: Placement = event.regFooterPlacement === 'outside' ? 'outside' : 'inside'
  const mobileAlignmentClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  }[event.regMobileFormAlignment || 'center'] || 'justify-center'
  const desktopAlignmentClass = {
    left: 'lg:justify-start',
    center: 'lg:justify-center',
    right: 'lg:justify-end',
  }[event.regFormAlignment || 'center'] || 'lg:justify-center'
  const desktopWidthClass = {
    narrow: 'lg:max-w-xl',
    medium: 'lg:max-w-2xl',
    wide: 'lg:max-w-4xl',
    full: 'lg:max-w-none',
  }[event.regFormWidth || 'medium'] || 'lg:max-w-2xl'
  const designShellWidthClass = event.regFormWidth === 'full' ? 'max-w-none' : 'max-w-[1440px]'
  const shellMaxWidth = useWideShell ? designShellWidthClass : 'max-w-2xl'
  const pageVariantClass = pageVariant ? `ev-reg-${pageVariant}-page` : ''
  const linkClass = registrationLinkClass(registrationLinkSlug)
  const layoutModeClass = hasBanner
    ? 'ev-reg-split-layout'
    : hasBackground
      ? 'ev-reg-background-layout'
      : ''
  const shellPaddingClass = ['form', 'agenda', 'confirmation'].includes(pageVariant || '')
    ? 'px-4 sm:px-6 lg:px-10'
    : 'px-4 py-8 sm:px-6 lg:px-10'
  const showLangSwitcher = event.regShowLangSwitcher && locales.length > 1
  const backgroundVars: React.CSSProperties | undefined =
    hasBanner && hasBackground
      ? ({
          '--ev-reg-bg-mobile-url': `url("${mobileBackgroundUrl}")`,
          '--ev-reg-bg-desktop-url': `url("${desktopBackgroundUrl}")`,
        } as React.CSSProperties)
      : undefined

  const themeStyle: React.CSSProperties = {
    '--ev-primary': event.primaryColor,
    '--ev-secondary': event.secondaryColor,
    '--ev-bg': event.regBgColor,
    '--ev-text': event.regTextColor,
    '--ev-card-bg': event.regCardBgColor,
    '--ev-btn': event.regButtonColor || event.primaryColor,
    '--ev-btn-text': event.regButtonTextColor || '#FFFFFF',
    '--ev-input-border': event.regInputBorderColor,
    fontFamily: resolvedFontFamily ? `${resolvedFontFamily}, 'NotoKufiArabic', sans-serif` : "'NotoKufiArabic', sans-serif",
  } as React.CSSProperties

  const switchLocale = (locale: string) => {
    const url = new URL(window.location.href)
    const parts = url.pathname.split('/')
    if (locales.includes(parts[1])) parts[1] = locale
    else parts.splice(1, 0, locale)
    url.pathname = parts.join('/')
    router.visit(url.toString(), { preserveState: false })
  }

  const backgroundLayer = hasBackground ? (
    <div className="ev-reg-background pointer-events-none absolute inset-x-0 top-0 z-0" aria-hidden>
      {mobileBackgroundUrl && (
        <img
          src={mobileBackgroundUrl}
          alt=""
          decoding="async"
          fetchPriority="high"
          className="ev-reg-background-mobile block w-full lg:hidden"
        />
      )}
      {desktopBackgroundUrl && (
        <img
          src={desktopBackgroundUrl}
          alt=""
          decoding="async"
          fetchPriority="high"
          className="ev-reg-background-desktop hidden w-full lg:block"
        />
      )}
    </div>
  ) : null

  const registrationColumn = (
    <div className="ev-reg-main ev-reg-design-content flex w-full flex-col">
      {headerPlacement === 'outside' && <RegistrationHeader event={event} placement="outside" />}
      <div className={`flex w-full ${mobileAlignmentClass} ${desktopAlignmentClass}`}>
        <div className={`ev-reg-body w-full ${desktopWidthClass}`}>
          {headerPlacement === 'inside' && <RegistrationHeader event={event} placement="inside" />}
          <div className="ev-reg-content">{children}</div>
          {footerPlacement === 'inside' && <RegistrationFooter event={event} placement="inside" />}
        </div>
      </div>
      {footerPlacement === 'outside' && <RegistrationFooter event={event} placement="outside" />}
    </div>
  )

  const registrationShell = (
    <div className={`ev-reg-shell ev-reg-design-shell relative z-10 mx-auto w-full ${shellMaxWidth} ${shellPaddingClass}`}>
      {registrationColumn}
    </div>
  )

  const footerImage = event.footerUrl ? (
    <div className={`ev-reg-page-footer-image w-full max-h-72 overflow-hidden ${hasBanner ? 'mt-auto' : ''}`}>
      <img src={event.footerUrl} alt={`${event.name} footer`} className="w-full object-cover" />
    </div>
  ) : null

  return (
    <AppShell {...shell} urls={urls} translations={t} fullWidth={useWideShell}>
      <Head title={pageTitle || `${t.register} – ${event.name}`}>
        {desktopBannerUrl && (
          <link rel="preload" as="image" href={desktopBannerUrl} media="(min-width: 768px)" />
        )}
        {mobileBannerUrl && (
          <link rel="preload" as="image" href={mobileBannerUrl} media="(max-width: 767px)" />
        )}
        {desktopBackgroundUrl && (
          <link rel="preload" as="image" href={desktopBackgroundUrl} media="(min-width: 1024px)" />
        )}
        {mobileBackgroundUrl && (
          <link rel="preload" as="image" href={mobileBackgroundUrl} media="(max-width: 1023px)" />
        )}
      </Head>

      {event.customFontUrl && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
          @font-face {
            font-family: '${resolvedFontFamily}';
            src: url('${event.customFontUrl}') format('truetype');
            font-weight: normal;
            font-style: normal;
            font-display: swap;
          }
        `,
          }}
        />
      )}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @font-face {
          font-family: 'NotoKufiArabic';
          src: url('/22015-notokufiarabic-regular.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
        .ev-reg-page {
          background-color: var(--ev-bg);
          color: var(--ev-text);
        }
        .ev-reg-split-layout {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          min-height: 100dvh;
        }
        .ev-reg-split-layout .ev-reg-design-stack {
          display: flex;
          flex-direction: column;
          width: 100%;
          flex: 1 1 auto;
          min-height: 0;
        }
        .ev-reg-split-layout .ev-reg-banner-image {
          display: block;
          width: 100%;
          height: auto;
        }
        .ev-reg-split-layout .ev-reg-page-body {
          position: relative;
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;
          min-height: 0;
          overflow: hidden;
          background-color: var(--ev-bg);
          isolation: isolate;
        }
        .ev-reg-split-layout .ev-reg-page-body-has-background {
          background-image: var(--ev-reg-bg-mobile-url);
          background-repeat: no-repeat;
          background-size: 100% auto;
          background-position: top center;
        }
        @media (min-width: 1024px) {
          .ev-reg-split-layout .ev-reg-page-body-has-background {
            background-image: var(--ev-reg-bg-desktop-url);
          }
        }
        .ev-reg-split-layout .ev-reg-page-body-content {
          position: relative;
          z-index: 2;
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;
          min-height: 0;
        }
        .ev-reg-split-layout .ev-reg-top-banner {
          position: relative;
          z-index: 20;
          flex-shrink: 0;
          line-height: 0;
        }
        .ev-reg-split-layout .ev-reg-background {
          display: none !important;
        }
        /* No banner: full-width artwork from the top of the page, scrolls with content */
        .ev-reg-background-layout {
          position: relative;
          min-height: 100vh;
          min-height: 100dvh;
          /* Stop in-flow form margin (e.g. custom margin-top) collapsing and shifting the background down */
          display: flow-root;
        }
        .ev-reg-background-layout .ev-reg-background {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: auto !important;
          width: 100% !important;
          height: auto !important;
          z-index: 0 !important;
          overflow: visible !important;
          pointer-events: none !important;
        }
        .ev-reg-background-layout .ev-reg-background img {
          position: relative !important;
          inset: auto !important;
          width: 100% !important;
          height: auto !important;
          min-height: 0 !important;
          object-fit: contain;
          object-position: top center;
        }
        .ev-reg-background-layout .ev-reg-background-mobile {
          display: block !important;
        }
        .ev-reg-background-layout .ev-reg-background-desktop {
          display: none !important;
        }
        @media (min-width: 1024px) {
          .ev-reg-background-layout .ev-reg-background-mobile {
            display: none !important;
          }
          .ev-reg-background-layout .ev-reg-background-desktop {
            display: block !important;
          }
        }
        .ev-reg-background-layout .ev-reg-shell,
        .ev-reg-background-layout .ev-reg-logo-wrap,
        .ev-reg-background-layout .ev-reg-lang-switcher-wrap,
        .ev-reg-background-layout .ev-reg-main {
          position: relative;
          z-index: 2;
        }
        .ev-reg-split-layout .ev-reg-page-body-has-background::before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0) 0%,
            rgba(0, 0, 0, 0.06) 12%,
            rgba(0, 0, 0, 0.18) 50%,
            rgba(0, 0, 0, 0.28) 100%
          );
        }
        .ev-reg-top-banner .ev-reg-banner-image {
          width: 100%;
          height: auto;
        }
        .ev-reg-steps-connected {
          gap: 0;
          flex-wrap: nowrap;
          justify-content: space-between;
          width: 100%;
        }
        .ev-reg-steps-connected .ev-reg-step {
          flex: 1;
          flex-direction: column;
          align-items: center;
          position: relative;
        }
        .ev-reg-steps-connected .ev-reg-step:not(:last-child)::after {
          content: "";
          position: absolute;
          top: 0.875rem;
          inset-inline-start: calc(50% + 0.875rem);
          width: calc(100% - 1.75rem);
          height: 2px;
          background-color: var(--ev-input-border);
          z-index: 0;
          pointer-events: none;
        }
        .ev-reg-step-num {
          position: relative;
          z-index: 2;
          background-color: var(--ev-card-bg, #eef2f1);
          color: var(--ev-text);
          border-color: var(--ev-input-border);
          opacity: 1;
          box-shadow: 0 0 0 2px var(--ev-card-bg, #eef2f1);
        }
        .ev-reg-step-pending .ev-reg-step-num {
          opacity: 0.65;
        }
        .ev-reg-step-active .ev-reg-step-num,
        .ev-reg-step-done .ev-reg-step-num {
          background-color: var(--ev-primary);
          color: #fff;
          border-color: var(--ev-primary);
          opacity: 1;
          box-shadow: none;
        }
        .ev-reg-step-label { color: var(--ev-text); opacity: 0.65; }
        .ev-reg-step-active .ev-reg-step-label { opacity: 1; }
        .ev-reg-card {
          background-color: var(--ev-card-bg);
          border-color: var(--ev-input-border);
          color: var(--ev-text);
        }
        .ev-reg-button {
          background-color: var(--ev-btn);
          color: var(--ev-btn-text);
        }
        .ev-reg-button-secondary {
          background-color: transparent;
          color: var(--ev-text);
          border-color: var(--ev-input-border);
        }
        .ev-reg-input {
          border-color: var(--ev-input-border);
          color: var(--ev-text);
          background-color: var(--ev-card-bg);
        }
        .ev-reg-session-item,
        .ev-reg-session-card,
        .ev-reg-session-timeline-item,
        .ev-reg-session-table-day {
          border-color: var(--ev-input-border);
          background-color: var(--ev-card-bg);
          color: var(--ev-text);
        }
        .ev-reg-session-table-header {
          border-color: var(--ev-input-border);
          color: var(--ev-text);
        }
        .ev-reg-session-table-row {
          border-color: var(--ev-input-border);
        }
        .ev-reg-session-table-from,
        .ev-reg-session-table-to {
          border-color: var(--ev-input-border);
        }
        .ev-reg-session-selected {
          border-color: var(--ev-primary);
        }
        .ev-reg-session-description,
        .ev-reg-session-day-title,
        .ev-reg-agenda-title {
          color: var(--ev-text);
        }
        .ev-reg-session-time {
          color: var(--ev-text);
          opacity: 0.65;
        }
        .ev-reg-session-clock {
          white-space: nowrap;
        }

        .ev-reg-session-clock-value {
          direction: ltr;
          unicode-bidi: isolate;
        }
        .ev-reg-session-time-badge {
          background-color: rgba(0, 0, 0, 0.06);
          color: var(--ev-text);
          opacity: 0.75;
        }
        .ev-reg-session-timeline-line {
          background-color: var(--ev-input-border);
        }
        .ev-reg-session-timeline-dot {
          border-color: var(--ev-input-border);
          background-color: var(--ev-card-bg);
        }
        .ev-reg-session-timeline-dot-selected {
          border-color: var(--ev-primary);
        }
        .ev-reg-tickets,
        .ev-reg-ticket,
        .ev-reg-entry-ticket {
          border-color: var(--ev-input-border);
          background-color: var(--ev-card-bg);
          color: var(--ev-text);
        }
        .ev-reg-tickets-title,
        .ev-reg-ticket-name,
        .ev-reg-entry-ticket-title {
          color: var(--ev-text);
        }
        .ev-reg-ticket-link {
          color: var(--ev-primary);
          text-decoration: none;
        }
        .ev-reg-agenda-pdf-view,
        .ev-reg-agenda-pdf-download {
          border-color: var(--ev-input-border);
          color: var(--ev-text);
        }
        .ev-reg-selected-sessions-panel {
          border-color: var(--ev-input-border);
        }
        .ev-reg-back-link-anchor {
          color: var(--ev-primary);
        }
        .ev-reg-qr-wrap .ev-reg-registration-key-label {
          color: #3d524e;
        }
        .ev-reg-qr-wrap .ev-reg-registration-key {
          color: #0a3d37;
        }
        .ev-reg-qr-wrap .ev-reg-registration-key-hint {
          color: #5a6b67;
        }
        /* Lang switcher: in document flow on mobile, overlay on desktop backgrounds */
        @media (max-width: 1023px) {
          .ev-reg-split-layout .ev-reg-lang-switcher-wrap,
          .ev-reg-background-layout .ev-reg-lang-switcher-wrap {
            position: relative;
            z-index: 3;
            flex-shrink: 0;
            padding: 0.75rem 1rem 0.5rem;
          }
          .ev-reg-has-lang-switcher.ev-reg-split-layout .ev-reg-shell,
          .ev-reg-has-lang-switcher.ev-reg-background-layout .ev-reg-shell {
            padding-top: 0.5rem;
          }
        }
        @media (min-width: 1024px) {
          .ev-reg-split-layout .ev-reg-lang-switcher-wrap,
          .ev-reg-background-layout .ev-reg-lang-switcher-wrap {
            position: absolute;
            top: 1rem;
            inset-inline-start: 1rem;
            inset-inline-end: auto;
            z-index: 20;
            padding: 0;
          }
        }
      `,
        }}
      />
      {event.regCustomCss && <style dangerouslySetInnerHTML={{ __html: event.regCustomCss }} />}

      <div
        className={['ev-reg-page', pageVariantClass, layoutModeClass, linkClass, showLangSwitcher ? 'ev-reg-has-lang-switcher' : '', 'relative isolate min-h-screen'].filter(Boolean).join(' ')}
        style={themeStyle}
        dir={currentLocale === 'ar' ? 'rtl' : 'ltr'}
        data-ev-reg-page={pageVariant || 'default'}
        data-registration-link={linkClass ? registrationLinkSlug?.trim().toLowerCase() : undefined}
      >
        {hasBanner ? (
          <div className="ev-reg-design-stack flex min-h-0 w-full flex-1 flex-col">
            <div className="ev-reg-banner ev-reg-top-banner w-full shrink-0">
              {mobileBannerUrl && (
                <img
                  src={mobileBannerUrl}
                  alt={event.name}
                  className="ev-reg-banner-image ev-reg-banner-mobile !block w-full md:!hidden"
                />
              )}
              {desktopBannerUrl && (
                <img
                  src={desktopBannerUrl}
                  alt={event.name}
                  className="ev-reg-banner-image ev-reg-banner-desktop !hidden w-full md:!block"
                />
              )}
            </div>
            <div
              className={`ev-reg-page-body${hasBackground ? ' ev-reg-page-body-has-background' : ''}`}
              style={backgroundVars}
            >
              <div className="ev-reg-page-body-content flex min-h-0 flex-1 flex-col">
                {showLangSwitcher && (
                  <LangSwitcher locales={locales} currentLocale={currentLocale} switchLocale={switchLocale} />
                )}
                {registrationShell}
                {footerImage}
              </div>
            </div>
          </div>
        ) : (
          <>
            {backgroundLayer}
            {event.logoUrl ? (
              <div className="ev-reg-logo-wrap flex justify-center pt-10 pb-2">
                <img src={event.logoUrl} alt={event.name} className="ev-reg-logo h-24 object-contain" />
              </div>
            ) : null}

            {showLangSwitcher && (
              <LangSwitcher locales={locales} currentLocale={currentLocale} switchLocale={switchLocale} />
            )}

            {registrationShell}

            {footerImage}
          </>
        )}
      </div>
    </AppShell>
  )
}
